import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { Users, Mail, BarChart3, MessageSquare, Download, UserPlus, Globe, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function exportCSV(data, columns, filename) {
  const header = columns.map(c => c.label).join(',');
  const rows = data.map(row =>
    columns.map(c => {
      const val = String(c.key.split('.').reduce((o, k) => o?.[k] ?? '', row) || '');
      return `"${val.replace(/"/g, '""')}"`;
    }).join(',')
  );
  const csv = [header, ...rows].join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
  URL.revokeObjectURL(url);
  toast.success(`${filename}.csv descargado`);
}

export default function AdminDashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [contacts, setContacts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [invLeads, setInvLeads] = useState([]);

  const loadData = useCallback(async () => {
    const opts = { credentials: 'include' };
    try {
      const [s, c, sub, inv] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/stats`, opts).then(r => r.json()),
        fetch(`${BACKEND_URL}/api/admin/contacts`, opts).then(r => r.json()),
        fetch(`${BACKEND_URL}/api/admin/subscribers`, opts).then(r => r.json()),
        fetch(`${BACKEND_URL}/api/admin/invitational`, opts).then(r => r.json()),
      ]);
      setStats(s); setContacts(c); setSubscribers(sub); setInvLeads(inv);
    } catch { toast.error('Error cargando datos'); }
  }, []);

  useEffect(() => {
    if (!authLoading && (!user || !user.is_admin)) { navigate('/'); return; }
    if (user?.is_admin) loadData();
  }, [user, authLoading, navigate, loadData]);

  const downloadQR = () => {
    const link = document.createElement('a');
    link.href = `${BACKEND_URL}/api/qr/invitational`;
    link.download = 'elite-golf-mx-invitational-qr.png';
    link.click();
  };

  if (authLoading) return (
    <div className="min-h-screen bg-[#050505] pt-20 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050505] pt-20" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="text-3xl md:text-4xl font-bold tracking-tighter text-white mb-8" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          Panel de Administracion
        </motion.h1>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
          {[
            { icon: Users, label: 'Usuarios', value: stats.total_users || 0 },
            { icon: Mail, label: 'Suscriptores', value: stats.total_subscribers || 0 },
            { icon: MessageSquare, label: 'Contactos', value: stats.total_contacts || 0 },
            { icon: UserPlus, label: 'Invitational', value: stats.total_invitational || 0 },
            { icon: BarChart3, label: 'Total Leads', value: (stats.total_contacts || 0) + (stats.total_invitational || 0) + (stats.total_subscribers || 0) },
          ].map((s) => (
            <div key={s.label} className="bg-[#0A120E] border border-white/5 p-5" data-testid={`stat-${s.label.toLowerCase()}`}>
              <s.icon size={16} className="text-zinc-600 mb-2" />
              <p className="text-2xl font-bold text-white">{s.value}</p>
              <p className="text-xs text-zinc-500">{s.label}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="invitational" className="w-full">
          <TabsList className="bg-[#0A120E] border border-white/5 mb-8">
            <TabsTrigger value="invitational" data-testid="tab-invitational">Invitational</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">Contactos</TabsTrigger>
            <TabsTrigger value="subscribers" data-testid="tab-subscribers">Suscriptores</TabsTrigger>
            <TabsTrigger value="qr" data-testid="tab-qr">QR Code</TabsTrigger>
          </TabsList>

          {/* INVITATIONAL TAB */}
          <TabsContent value="invitational">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-zinc-500">{invLeads.length} aplicacion{invLeads.length !== 1 ? 'es' : ''}</p>
              <div className="flex items-center gap-2">
                <button onClick={() => exportCSV(invLeads, [
                    { label: 'Nombre', key: 'full_name' }, { label: 'WhatsApp', key: 'whatsapp' },
                    { label: 'Email', key: 'email' }, { label: 'Handicap', key: 'handicap' },
                    { label: 'Club', key: 'home_club' }, { label: 'Viaje Internacional', key: 'travel_international' },
                    { label: 'Status', key: 'status' }, { label: 'Fecha', key: 'created_at' },
                  ], 'invitational_leads')}
                  className="text-xs text-zinc-400 hover:text-white border border-white/10 px-3 py-1.5 transition-colors flex items-center gap-1.5" data-testid="export-inv-csv">
                  <FileDown size={12} /> Exportar CSV
                </button>
                <button onClick={() => { navigator.clipboard.writeText('https://elitegolfmx.mx/invitational'); toast.success('Link copiado'); }}
                  className="text-xs text-zinc-400 hover:text-white border border-white/10 px-3 py-1.5 transition-colors flex items-center gap-1.5" data-testid="copy-inv-link">
                  <Globe size={12} /> Copiar Link
                </button>
              </div>
            </div>
            <div className="space-y-2">
              {invLeads.map(lead => (
                <div key={lead.lead_id} className="p-5 bg-[#0A120E] border border-white/5" data-testid={`inv-lead-${lead.lead_id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-white">{lead.full_name}</span>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 ${lead.status === 'new' ? 'bg-emerald-900/30 text-emerald-400 border border-emerald-800/30' : 'bg-zinc-800 text-zinc-400 border border-zinc-700'}`}>
                        {lead.status}
                      </span>
                      <span className="text-xs text-zinc-600">{lead.created_at?.split('T')[0]}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-zinc-400">
                    <div><span className="text-zinc-600">WhatsApp:</span> <a href={`https://wa.me/${lead.whatsapp?.replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer" className="text-white hover:underline">{lead.whatsapp}</a></div>
                    <div><span className="text-zinc-600">Email:</span> {lead.email}</div>
                    <div><span className="text-zinc-600">Handicap:</span> {lead.handicap || '—'}</div>
                    <div><span className="text-zinc-600">Club:</span> {lead.home_club || '—'}</div>
                  </div>
                  {lead.travel_international && (
                    <p className="text-xs text-zinc-600 mt-2">Viaje internacional: <span className="text-zinc-400">{lead.travel_international}</span></p>
                  )}
                </div>
              ))}
              {invLeads.length === 0 && <p className="text-center text-zinc-500 py-12">No hay aplicaciones invitational</p>}
            </div>
          </TabsContent>

          {/* CONTACTS TAB */}
          <TabsContent value="contacts">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-zinc-500">{contacts.length} mensaje{contacts.length !== 1 ? 's' : ''}</p>
              {contacts.length > 0 && (
                <button onClick={() => exportCSV(contacts, [
                    { label: 'Nombre', key: 'name' }, { label: 'Email', key: 'email' },
                    { label: 'Telefono', key: 'phone' }, { label: 'Mensaje', key: 'message' },
                    { label: 'Grupo', key: 'group_size' }, { label: 'Presupuesto', key: 'budget' },
                    { label: 'Fecha', key: 'created_at' },
                  ], 'contactos')}
                  className="text-xs text-zinc-400 hover:text-white border border-white/10 px-3 py-1.5 transition-colors flex items-center gap-1.5" data-testid="export-contacts-csv">
                  <FileDown size={12} /> Exportar CSV
                </button>
              )}
            </div>
            <div className="space-y-2">
              {contacts.map(c => (
                <div key={c.submission_id} className="p-5 bg-[#0A120E] border border-white/5" data-testid={`contact-${c.submission_id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-white">{c.name}</span>
                    <span className="text-xs text-zinc-600">{c.created_at?.split('T')[0]}</span>
                  </div>
                  <p className="text-xs text-zinc-500 mb-2">{c.email} {c.phone && `| ${c.phone}`}</p>
                  <p className="text-sm text-zinc-400 leading-relaxed">{c.message}</p>
                </div>
              ))}
              {contacts.length === 0 && <p className="text-center text-zinc-500 py-12">No hay mensajes de contacto</p>}
            </div>
          </TabsContent>

          {/* SUBSCRIBERS TAB */}
          <TabsContent value="subscribers">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-zinc-500">{subscribers.length} suscriptor{subscribers.length !== 1 ? 'es' : ''}</p>
              {subscribers.length > 0 && (
                <button onClick={() => exportCSV(subscribers, [
                    { label: 'Email', key: 'email' }, { label: 'Fecha', key: 'created_at' },
                  ], 'suscriptores')}
                  className="text-xs text-zinc-400 hover:text-white border border-white/10 px-3 py-1.5 transition-colors flex items-center gap-1.5" data-testid="export-subs-csv">
                  <FileDown size={12} /> Exportar CSV
                </button>
              )}
            </div>
            <div className="space-y-2">
              {subscribers.map(s => (
                <div key={s.subscriber_id} className="flex items-center justify-between p-4 bg-[#0A120E] border border-white/5" data-testid={`subscriber-${s.subscriber_id}`}>
                  <span className="text-sm text-white">{s.email}</span>
                  <span className="text-xs text-zinc-600">{s.created_at?.split('T')[0]}</span>
                </div>
              ))}
              {subscribers.length === 0 && <p className="text-center text-zinc-500 py-12">No hay suscriptores</p>}
            </div>
          </TabsContent>

          {/* QR CODE TAB */}
          <TabsContent value="qr">
            <div className="max-w-md mx-auto text-center space-y-8 py-8">
              <div>
                <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>QR Code — Invitational Access</h3>
                <p className="text-sm text-zinc-500">Descarga este QR para imprimirlo en tarjetas de presentacion, flyers o material fisico.</p>
              </div>
              <div className="bg-black border border-white/10 p-8 inline-block mx-auto">
                <img
                  src={`${BACKEND_URL}/api/qr/invitational`}
                  alt="QR Code - Elite Golf MX Invitational"
                  className="w-64 h-64 mx-auto"
                  data-testid="qr-code-image"
                />
              </div>
              <div className="space-y-3">
                <button onClick={downloadQR}
                  className="bg-white text-black px-6 py-3 text-sm font-medium hover:bg-zinc-200 transition-colors inline-flex items-center gap-2"
                  data-testid="download-qr-btn"
                >
                  <Download size={16} /> Descargar QR (PNG)
                </button>
                <p className="text-xs text-zinc-600">Apunta a: elitegolfmx.mx/invitational</p>
              </div>
              <div className="border border-white/5 p-4 text-left space-y-2">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Instrucciones de Google Sheets</p>
                <p className="text-xs text-zinc-400 leading-relaxed">
                  Para sincronizar leads automaticamente con Google Sheets:
                </p>
                <ol className="text-xs text-zinc-400 leading-relaxed list-decimal list-inside space-y-1">
                  <li>Crea una Google Sheet con columnas: source, full_name, whatsapp, email, handicap, home_club, travel_international, date</li>
                  <li>Ve a Extensions &gt; Apps Script</li>
                  <li>Pega el script de webhook (disponible en documentacion)</li>
                  <li>Publica como Web App (acceso: Anyone)</li>
                  <li>Copia la URL y agregala como GOOGLE_SHEETS_WEBHOOK en el backend .env</li>
                </ol>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
